/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for any purpose without the express written permission of Honeywell. */ import { createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import jwt_decode from "jwt-decode";
import store, { AppDispatch } from "..";
import { ConfigurationVariables } from "../../config";
import type { Role, UserDto } from "../../models";
import OrganizationIdName from "../../models/apiModels/OrganizationIdName";
import { userActions } from "./user-slice";
import { UUID } from "../../models/apiModels/Organization";

//jwt decode

interface UserToken {
  sub: number;
  ext: {
    customer: string;
    tenant_id: string;
  };
}

interface UserRoleResponse {
  roleId: string;
  name: string;
}

/// Fetch users based on tenantId
export const getUserListBasedOnForgeCustomerId = createAsyncThunk(
  "user/getUserListBasedOnForgeCustomerId",
  async (payload: string | { id: string; silent?: boolean }, { rejectWithValue }: any) => {
    // Handle both string and object payload for backward compatibility
    const id = typeof payload === 'string' ? payload : payload.id;
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };

      const response: AxiosResponse<any> = await axios.get(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.RouteTenants}/${id}/${ConfigurationVariables.UserController}`,
        config
      );
      if (response?.status !== 200) {
        return rejectWithValue(response);
      }
      if (response?.data?.length > 0) {
        const responseWIthId = response?.data?.map((x: any) => ({
          ...x,
          id: x.userId,
        }));
        return responseWIthId;
      } else {
        return response.data;
      }
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);
export const getOrganizationUserByTenantID = createAsyncThunk(
  "organization/getOrganizationUserByTenantID",

  async (
    sendOrgData: { tenantID: string; organizationId: UUID },
    { rejectWithValue }
  ) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };
      const response: AxiosResponse<any> = await axios.get(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.RouteTenants}/${sendOrgData.tenantID}/${ConfigurationVariables.OrganisationController}/${sendOrgData.organizationId}/${ConfigurationVariables.UserController}`,
        config
      );
      if (response?.status === 200) {
        const responseWithId = response.data.map((x: any) => ({
          ...x,
          id: x.userId,
        }));

        return {
          originalUserData: responseWithId,
        };
      } else {
        return rejectWithValue(response);
      }
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

/// Delete user
export const deleteTenantUser = createAsyncThunk(
  "tenant/user/deleteUser",
  async (
    userToDelete: {
      customerId: string;
      userIds: Array<string>;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.delete(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.RouteTenants}/${userToDelete.customerId}/${ConfigurationVariables.UserController}`,
        {
          data: { userId: userToDelete.userIds },
          headers: {
            Authorization: "Bearer " + getAccessToken(),
          },
        }
      );

      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return userToDelete;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

// Deactivate user
export const deactivateUser = createAsyncThunk(
  "tenant/user/deactivateUser",
  async (
    userToDeactivate: {
      tenantId: string;
      userId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
        },
      };
      const { tenantId, userId } = userToDeactivate;
      const { UserMgmt_Endpoints, Api, Version, RouteTenants } = ConfigurationVariables;
      const response = await axios.put(
        `${UserMgmt_Endpoints}/${Api}/${Version}/${RouteTenants}/${tenantId}/users/${userId}/deactivateuser`,
        {},
        config
      );

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

// Reactivate user
export const reactivateUser = createAsyncThunk(
  "tenant/user/reactivateUser",
  async (
    userToReactivate: {
      tenantId: string;
      userId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
        },
      };

      const { tenantId, userId } = userToReactivate;
      const { UserMgmt_Endpoints, Api, Version, RouteTenants } = ConfigurationVariables;
      const response = await axios.put(
        `${UserMgmt_Endpoints}/${Api}/${Version}/${RouteTenants}/${tenantId}/users/${userId}/reactivateuser`,
        {},
        config
      );

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const resetUserPassword = createAsyncThunk(
  "tenant/user/resetUserPassword",
  async (
    userToResetPassword: {
      tenantId: string;
      userId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
        },
      };
      const { tenantId, userId } = userToResetPassword;
      const { UserMgmt_Endpoints, Api, Version, RouteTenants } = ConfigurationVariables;
      const response = await axios.post(
        `${UserMgmt_Endpoints}/${Api}/${Version}/${RouteTenants}/${tenantId}/users/${userId}/resetpassword`,
        {},
        config
      );

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

// Fetch user export list based on tenantId
export const getUserExportListBasedOnForgeCustomerId = createAsyncThunk(
  "user/getUserExportListBasedOnForgeTenantId",
  async (forgeTenantId: string, { rejectWithValue }: any) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };

      const response: AxiosResponse<any> = await axios.get(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.UserController}`,
        config
      );
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      if (response.data.length > 0) {
        const excelConversion = response.data.map(
          (user: {
            userId: any;
            firstName: any;
            lastName: any;
            emailId: any;
            roles: any;
            organization: any;
          }) => ({
            "User ID": user.userId,
            "First Name": user.firstName,
            "Last Name": user.lastName,
            "Email Id": user.emailId,
            Roles: user.roles.map((role: any) => role.name).join(", "),
            Organization: user.organization.map((org: any) =>
              org.name.join(", ")
            ),
          })
        );
        return excelConversion;
      }
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const getAccessToken = () => {
  if (
    (sessionStorage.getItem("oidc.default") as any) === undefined ||
    (sessionStorage.getItem("oidc.default") as any) === null ||
    (sessionStorage.getItem("oidc.default") as any) === ""
  ) {
    return "";
  } else {
    const accessToken: string = JSON.parse(
      sessionStorage.getItem("oidc.default") as any
    )["tokens"]["accessToken"];
    if (
      accessToken === undefined ||
      accessToken === null ||
      accessToken === ""
    ) {
      throw new Error("Access token is not in valid format");
    }
    return accessToken;
  }
};

//Set Role list for filter
export const setroleList = (uniqueRoles: Array<any>) => {
  return (dispatch: AppDispatch) => {
    dispatch(userActions.showRoleList(uniqueRoles));
  };
};

export const selectedRoles = (selectedValue: string[]) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      userActions.filteredRoleList({
        selectedValue: selectedValue,
        filterApplied: selectedValue.length === 0 ? false : true,
      })
    );
  };
};

export const setFilteredUsers = (FiltredUsers: Array<UserDto>) => {
  return (dispatch: AppDispatch) => {
    dispatch(userActions.setFilteredUserList(FiltredUsers));
  };
};

/// Fetch User export list
export const exportUserListBasedOnForgeCustomerId = createAsyncThunk(
  "user/exportUserListBasedOnForgeCustomerId",
  async (forgeCustomerId: string, { rejectWithValue }: any) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };
      const response: any = await axios.get(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.RouteTenants}/${forgeCustomerId}/${ConfigurationVariables.UserController}`,
        config
      );
      if (response.status !== 200) {
        return rejectWithValue(response);
      }

      type Role = {
        roleId: string;
        name: string;
      };

      type UserT = {
        userId: string;
        firstName: string;
        lastName: string;
        emailId: string;
        phone: string;
        roles: Role[];
        organization: OrganizationIdName[];
      };

      type ConvertedData = {
        "User Id": string;
        roles: string;
      };

      const convertedData: ConvertedData[] = response.data.map((data: any) => {
        const Roles: string = data.roles
          .map((role: Role) => role.name)
          .join(",");
        const Orgs: string = data.organization
          .map((org: any) => org.name)
          .join(", ");
        return {
          "User Id": data.userId,
          Roles,
          "First Name": data.firstName,
          "Last Name": data.lastName,
          "Email ID": data.emailId,
          Orgs,
        };
      });

      return convertedData;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);
// End Fetch Custoemr Export List

/// Fetch users Permissions for logged in user
export const getUserPermissions = createAsyncThunk(
  "users/userpermissions",
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };

      const response: AxiosResponse<any> = await axios.get(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.RouteTenants}/${tenantId}/${ConfigurationVariables.RouteUserPermissions}`,
        config
      );

      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      if (response.data) {
        return response.data;
      }
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

// Fetch user export list based on tenantId
export const createUser = createAsyncThunk(
  "user/createUser",
  async (userPostData: any, { rejectWithValue }: any) => {
    // CID 3180026
    if (!userPostData) {
      return rejectWithValue(new Error("userPostData is null or undefined"));
    }

    try {
      const postData = {
        firstName: userPostData.firstName,
        lastName: userPostData.lastName,
        emailId: userPostData.emailId,
        phoneNumber: userPostData.phoneNumber,
        roleId: userPostData.roleId,
        organizationId: userPostData.organizationId,
        customerId: userPostData.customerId,
        sso_enabled: userPostData.sso_enabled,
      };
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };

      const query = userPostData?.customerId
        ? `?customerId=${userPostData.customerId}`
        : "";
      const response: AxiosResponse<any> = await axios.post(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.RouteTenants}/${userPostData.tenantid}/${ConfigurationVariables.UserController}`,
        postData,
        config
      );

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);
//// Upload logo for user
export const uploadUserLogoDetails = createAsyncThunk(
  "user/uploadOrgLogoDetails",
  async (
    parameters: { userId: string; logo: any; customerId: string },
    { rejectWithValue }
  ) => {
    try {
      let formData = new FormData();
      formData.append("logo", parameters.logo);

      const config = {
        headers: {
          Accept: "application/json",
          Authorization: "Bearer " + getAccessToken(),
          // customerId: parameters.customerId,
        },
      };

      const response = await axios.put(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.RouteTenants}/${parameters.customerId}/${ConfigurationVariables.UserController}/${parameters.userId}/userpic`,
        formData,
        config
      );

      if (response.status !== 200) {
        return rejectWithValue(response);
      }

      return response;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

//// Upload logo fro organization
export const fetchLogoOrganization = createAsyncThunk(
  "organization/fetchLogoOrganization",
  async (organizationId: UUID, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };
      const response = await axios.get(
        `${ConfigurationVariables.TenantMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.OrganisationController}/logo`,
        config
      );

      if (response.status !== 200) {
        return rejectWithValue(response);
      }

      return response;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const editUser = createAsyncThunk(
  "user/editUser",
  async (userPostData: any, { rejectWithValue }: any) => {
    try {
      // CID 3263531
      if (!userPostData) {
        throw new Error("userPostData is null or undefined");
      }

      const postData = {
        phoneNumber: userPostData.phoneNumber,
        organizations: userPostData.organizations ?? [],
        roles: userPostData.roles ?? [],
      };
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };

      const query = userPostData.customerId
        ? `?customerId=${userPostData.customerId}`
        : "";
      const response: AxiosResponse<any> = await axios.patch(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.RouteTenants}/${userPostData.tenantid}/${ConfigurationVariables.UserController}/${userPostData.userId}${query}`,
        postData,
        config
      );

      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      if (response.data) {
        return userPostData;
      }
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

//// Upload logo fro organization
export const fetchLogoUser = createAsyncThunk(
  "user/fetchLogoUser",
  async (args: { userId: string; customerId: string }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
          customerId: args.customerId,
        },
      };

      const response = await axios.get(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.RouteTenants}/${args.customerId}/${ConfigurationVariables.UserController}/${args.userId}/userpic`,
        config
      );

      if (response.status !== 200) {
        return rejectWithValue(response);
      }

      return response;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const getUserRolesbyOrg = createAsyncThunk(
  "user/getUserRolesbyOrg",
  async (args: { orgId?: UUID; tenantId?: string }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };

      const queryTenant = args.tenantId! ? `?tenantId=${args.tenantId}` : "";
      const queryOrg = args.orgId! ? `organizationId=${args.orgId}` : "";
      const response = await axios.get(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${
          ConfigurationVariables.Api
        }/${ConfigurationVariables.Version}/roles${
          queryTenant + "&" + queryOrg
        }`,
        config
      );

      return response;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

// get user logo
export const getUserLogo = createAsyncThunk(
  "user/getUserLogo",
  async (args: { userId: string; customerId: string }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
          customerId: args.customerId,
        },
      };

      const response = await axios.get(
        `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.RouteTenants}/${args.customerId}/${ConfigurationVariables.UserController}/${args.userId}/userpic`,
        config
      );

      return response;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

// Fetch user role/persona from API
export const getUserRole = createAsyncThunk(
  "users/getUserRole",
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
        },
      };

      const { UserMgmt_Endpoints, Api, Version, RouteTenants } = ConfigurationVariables;
      const response: AxiosResponse<UserRoleResponse> = await axios.get(
        `${UserMgmt_Endpoints}/${Api}/${Version}/${RouteTenants}/${tenantId}/GetUserRole`,
        config
      );

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

